import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { formatPlayCount } from '../lib/utils';
import { supabase } from '../lib/supabase';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };

const INPUT = 'w-full px-3 py-2 text-[13px] border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3fb5d2] focus:border-transparent text-[#3b3b3b] placeholder-[#c0c0c0] bg-white';
const LABEL = 'block text-[12px] font-medium text-[#646464] mb-1';

const STATUS_STYLES = {
  pending:   'bg-amber-50 text-amber-700 border border-amber-200',
  approved:  'bg-blue-50 text-blue-600 border border-blue-200',
  monitored: 'bg-green-50 text-green-700 border border-green-200',
  verified:  'bg-purple-50 text-purple-700 border border-purple-200',
  rejected:  'bg-red-50 text-[#c62828] border border-red-200',
  closed:    'bg-gray-100 text-[#8a8a8a] border border-[#e5e5e5]',
};

const PAYOUT_STYLES = {
  campaign_active: 'bg-blue-50 text-blue-600 border border-blue-200',
  to_be_settled:   'bg-amber-50 text-amber-700 border border-amber-200',
  paid:            'bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30',
};
const PAYOUT_LABELS = {
  campaign_active: 'Campaign Active',
  to_be_settled:   'To Be Settled',
  paid:            'Paid',
};

const fmtCurrency = (n) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const TABS = [
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'proposals', label: 'Proposals' },
  { key: 'posts',     label: 'Post Links' },
  { key: 'payouts',   label: 'Payouts' },
  { key: 'settings',  label: 'Settings' },
];

const Profile = () => {
  const { user, profile: authProfile } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('campaigns');

  const [formData, setFormData] = useState({ full_name: '', phone: '', bank_account: '', npwp: '', ktp: '' });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => { loadProfile(); }, []); // eslint-disable-line

  useEffect(() => {
    if (profileData?.profile) {
      setFormData({
        full_name:    profileData.profile.full_name    || '',
        phone:        profileData.profile.phone        || '',
        bank_account: profileData.profile.bank_account || '',
        npwp:         profileData.profile.npwp         || '',
        ktp:          profileData.profile.ktp          || '',
      });
    }
  }, [profileData]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/profile/me');
      setProfileData(data);
    } catch (error) {
      alert('Error loading profile: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange    = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
  const handlePasswordChange = (e) => setPasswordData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('File harus berupa gambar'); return; }
    if (file.size > 5 * 1024 * 1024)    { alert('Ukuran file maksimal 5MB'); return; }
    try {
      setUploadingImage(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id || authProfile?.user_id || 'user'}-${Date.now()}.${fileExt}`;
      const filePath = `profile-images/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('profiles').upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (uploadError) {
        if (uploadError.message?.includes('Bucket') || uploadError.message?.includes('not found')) {
          alert('Bucket storage belum dibuat. Silakan buat bucket "profiles" di Supabase Dashboard terlebih dahulu.');
        }
        throw uploadError;
      }
      const { data: urlData } = supabase.storage.from('profiles').getPublicUrl(filePath);
      await api.patch('/api/profile/me', { profile_image_url: urlData.publicUrl });
      await loadProfile();
      alert('Gambar profile berhasil diupload');
    } catch (error) {
      alert('Error uploading image: ' + (error.message || 'Unknown error'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await api.patch('/api/profile/me', formData);
      await loadProfile();
      alert('Profile berhasil diperbarui');
    } catch (error) {
      alert('Error saving profile: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) { alert('Password baru dan konfirmasi tidak cocok'); return; }
    if (passwordData.new_password.length < 6) { alert('Password baru minimal 6 karakter'); return; }
    try {
      setChangingPassword(true);
      await api.post('/api/profile/change-password', { current_password: passwordData.current_password, new_password: passwordData.new_password });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      alert('Password berhasil diubah');
    } catch (error) {
      alert('Error changing password: ' + (error.message || 'Unknown error'));
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profileData) {
    return <div className="text-center py-12 text-[#8a8a8a]">Profile not found</div>;
  }

  const initials = (profileData.profile?.full_name || profileData.profile?.email || 'U')[0].toUpperCase();

  return (
    <div className="space-y-4">
      <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Profile</h1>

      {/* Profile header card */}
      <div className="bg-white rounded-2xl p-6 flex items-center gap-5" style={card}>
        {profileData.profile?.profile_image_url ? (
          <img src={profileData.profile.profile_image_url} alt="Profile"
            className="w-16 h-16 rounded-full object-cover ring-2 ring-[#3fb5d2]/30 flex-shrink-0" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-[#3fb5d2] flex items-center justify-center text-white text-[22px] font-bold flex-shrink-0">
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[18px] font-semibold text-[#3b3b3b] truncate">
            {profileData.profile?.full_name || profileData.profile?.email || '—'}
          </p>
          <p className="text-[13px] text-[#8a8a8a] mt-0.5">{profileData.profile?.email}</p>
          {profileData.profile?.role && (
            <span className="inline-flex mt-1.5 items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30 capitalize">
              {profileData.profile.role}
            </span>
          )}
        </div>
        {profileData.tier && (
          <div className="hidden sm:block text-right flex-shrink-0">
            <p className="text-[11px] text-[#8a8a8a] uppercase tracking-wider mb-0.5">Current Tier</p>
            <p className="text-[16px] font-bold text-[#3fb5d2]">{profileData.tier.nama}</p>
            <p className="text-[12px] text-[#8a8a8a]">{formatPlayCount(profileData.total_play_count || 0)} views</p>
          </div>
        )}
      </div>

      {/* Tier card — mobile / when no tier in header */}
      {profileData.tier && (
        <div className="sm:hidden bg-[#ecf8fb] rounded-2xl p-4 border border-[#3fb5d2]/20" style={card}>
          <p className="text-[11px] text-[#2a798c] uppercase tracking-wider font-medium mb-1">Current Tier</p>
          <p className="text-[18px] font-bold text-[#3fb5d2]">{profileData.tier.nama}</p>
          <p className="text-[12px] text-[#8a8a8a]">{formatPlayCount(profileData.total_play_count || 0)} total views</p>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-2xl" style={card}>
        <div className="flex border-b border-[#e5e5e5] overflow-x-auto scrollbar-hide px-2">
          {TABS.map(({ key, label }) => {
            const count = key === 'campaigns' ? profileData.campaigns?.length
                        : key === 'proposals' ? profileData.proposals?.length
                        : key === 'posts'     ? profileData.postLinks?.length
                        : key === 'payouts'   ? profileData.payouts?.length
                        : null;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-4 py-3 text-[13px] font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === key
                    ? 'border-[#3fb5d2] text-[#3fb5d2]'
                    : 'border-transparent text-[#8a8a8a] hover:text-[#646464]'
                }`}
              >
                {label}{count != null ? ` (${count})` : ''}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {/* Campaigns Tab */}
          {activeTab === 'campaigns' && (
            profileData.campaigns?.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {profileData.campaigns.map((c) => (
                  <Link key={c.id} to={`/campaigns/${c.id}`}
                    className="block border border-[#e5e5e5] rounded-xl p-4 hover:border-[#3fb5d2]/40 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-[14px] font-semibold text-[#3b3b3b] leading-5">{c.name}</p>
                      {c.status === 'stopped' ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-[#8a8a8a] flex-shrink-0">Closed</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#ecf8fb] text-[#2a798c] flex-shrink-0">Active</span>
                      )}
                    </div>
                    <p className="text-[12px] text-[#8a8a8a] line-clamp-2">{c.description}</p>
                    <p className="text-[11px] text-[#8a8a8a] mt-2">{fmtDate(c.registered_at || c.created_at)}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <p className="text-[14px] text-[#8a8a8a]">Belum bergabung campaign apapun.</p>
                <Link to="/campaigns" className="mt-2 inline-block text-[13px] text-[#3fb5d2] hover:text-[#2a798c] font-medium">Browse Campaigns</Link>
              </div>
            )
          )}

          {/* Proposals Tab */}
          {activeTab === 'proposals' && (
            profileData.proposals?.length > 0 ? (
              <div className="divide-y divide-[#e5e5e5]">
                {profileData.proposals.map((p) => (
                  <div key={p.id} className="py-4 first:pt-0 last:pb-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2 mb-1.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_STYLES[p.display_status || p.status] || 'bg-gray-100 text-[#646464]'}`}>
                            {p.display_status || p.status}
                          </span>
                          {p.campaigns && (
                            <Link to={`/campaigns/${p.campaign_id}`} className="text-[13px] text-[#3fb5d2] hover:text-[#2a798c] font-medium truncate">
                              {p.campaigns.name}
                            </Link>
                          )}
                        </div>
                        {p.social_media_link && (
                          <a href={p.social_media_link} target="_blank" rel="noopener noreferrer"
                            className="text-[12px] text-[#3fb5d2] hover:text-[#2a798c] truncate block">{p.social_media_link}</a>
                        )}
                        <p className="text-[11px] text-[#8a8a8a] mt-1">{fmtDate(p.created_at)}</p>
                      </div>
                      <Link to={`/proposals/${p.id}`}
                        className="flex-shrink-0 text-[12px] text-[#3fb5d2] hover:text-[#2a798c] font-medium border border-[#3fb5d2]/40 rounded-lg px-3 py-1.5 transition-colors">
                        Detail
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <p className="text-[14px] text-[#8a8a8a]">Belum ada proposal.</p>
              </div>
            )
          )}

          {/* Post Links Tab */}
          {activeTab === 'posts' && (
            profileData.postLinks?.length > 0 ? (
              <div className="divide-y divide-[#e5e5e5]">
                {profileData.postLinks.map((p) => (
                  <div key={p.id} className="py-4 first:pt-0 last:pb-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2 mb-1.5">
                          {p.social_media_platform && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30">
                              {p.social_media_platform}
                            </span>
                          )}
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_STYLES[p.display_status || p.status] || 'bg-gray-100 text-[#646464]'}`}>
                            {p.display_status || p.status}
                          </span>
                          {p.campaigns && (
                            <Link to={`/campaigns/${p.campaign_id}`} className="text-[13px] text-[#3fb5d2] hover:text-[#2a798c] font-medium truncate">
                              {p.campaigns.name}
                            </Link>
                          )}
                        </div>
                        {p.social_media_link && (
                          <a href={p.social_media_link} target="_blank" rel="noopener noreferrer"
                            className="text-[12px] text-[#3fb5d2] hover:text-[#2a798c] truncate block">{p.social_media_link}</a>
                        )}
                        {p.play_count != null && (
                          <div className="flex flex-wrap gap-4 mt-2">
                            {[['Views', p.play_count], ['Likes', p.like_count], ['Comments', p.comment_count], ['Shares', p.share_count]].map(([lbl, val]) => (
                              <div key={lbl}>
                                <span className="text-[11px] text-[#8a8a8a]">{lbl}: </span>
                                <span className="text-[12px] font-semibold text-[#3b3b3b]">{formatPlayCount(val || 0)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="text-[11px] text-[#8a8a8a] mt-1">{fmtDate(p.social_media_posted_at || p.created_at)}</p>
                      </div>
                      <Link to={`/proposals/${p.id}`}
                        className="flex-shrink-0 text-[12px] text-[#3fb5d2] hover:text-[#2a798c] font-medium border border-[#3fb5d2]/40 rounded-lg px-3 py-1.5 transition-colors">
                        Detail
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <p className="text-[14px] text-[#8a8a8a]">Belum ada post link.</p>
              </div>
            )
          )}

          {/* Payouts Tab */}
          {activeTab === 'payouts' && (
            profileData.payouts?.length > 0 ? (
              <div className="divide-y divide-[#e5e5e5]">
                {profileData.payouts.map((p) => (
                  <div key={p.id} className="py-4 first:pt-0 last:pb-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2 mb-1.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${PAYOUT_STYLES[p.status] || 'bg-gray-100 text-[#646464]'}`}>
                            {PAYOUT_LABELS[p.status] || p.status}
                          </span>
                          {p.campaigns && (
                            <Link to={`/campaigns/${p.campaign_id}`} className="text-[13px] text-[#3fb5d2] hover:text-[#2a798c] font-medium truncate">
                              {p.campaigns.name}
                            </Link>
                          )}
                        </div>
                        {p.proposals?.social_media_link && (
                          <a href={p.proposals.social_media_link} target="_blank" rel="noopener noreferrer"
                            className="text-[12px] text-[#3fb5d2] hover:text-[#2a798c] truncate block mb-1.5">{p.proposals.social_media_link}</a>
                        )}
                        <div className="flex flex-wrap gap-4">
                          <div>
                            <span className="text-[11px] text-[#8a8a8a]">Amount: </span>
                            <span className="text-[13px] font-bold text-[#3b3b3b]">{fmtCurrency(p.amount)}</span>
                          </div>
                          {p.tiers && (
                            <div>
                              <span className="text-[11px] text-[#8a8a8a]">Tier: </span>
                              <span className="text-[12px] font-semibold text-[#3b3b3b]">{p.tiers.nama}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-[11px] text-[#8a8a8a] mt-1">{fmtDate(p.paid_at || p.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <p className="text-[14px] text-[#8a8a8a]">Belum ada payout.</p>
                <p className="text-[12px] text-[#8a8a8a] mt-1">Payout muncul setelah post link terverifikasi dan play count mencapai target.</p>
              </div>
            )
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6 max-w-xl">
              {/* Profile Info */}
              <div>
                <h3 className="text-[15px] font-semibold text-[#3b3b3b] mb-4">Informasi Profile</h3>

                {/* Photo upload */}
                <div className="flex items-center gap-4 mb-5">
                  {profileData.profile?.profile_image_url ? (
                    <img src={profileData.profile.profile_image_url} alt="Profile"
                      className="w-16 h-16 rounded-full object-cover ring-2 ring-[#3fb5d2]/30 flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-[#3fb5d2] flex items-center justify-center text-white text-[22px] font-bold flex-shrink-0">
                      {initials}
                    </div>
                  )}
                  <div>
                    <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold rounded-lg transition-colors">
                      {uploadingImage ? 'Uploading...' : 'Ganti Foto'}
                      <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploadingImage} className="hidden" />
                    </label>
                    <p className="text-[11px] text-[#8a8a8a] mt-1">Maks 5MB · JPG, PNG</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {[
                    { name: 'full_name',    label: 'Nama Lengkap',       type: 'text',  placeholder: 'Masukkan nama lengkap' },
                    { name: 'phone',        label: 'Nomor Telepon',      type: 'tel',   placeholder: 'Masukkan nomor telepon' },
                    { name: 'npwp',         label: 'NPWP',               type: 'text',  placeholder: 'Masukkan nomor NPWP' },
                    { name: 'ktp',          label: 'Nomor KTP',          type: 'text',  placeholder: 'Masukkan nomor KTP' },
                  ].map(({ name, label, type, placeholder }) => (
                    <div key={name}>
                      <label className={LABEL}>{label}</label>
                      <input type={type} name={name} value={formData[name]} onChange={handleInputChange} placeholder={placeholder} className={INPUT} />
                    </div>
                  ))}
                  <div>
                    <label className={LABEL}>Data Rekening Bank</label>
                    <textarea name="bank_account" value={formData.bank_account} onChange={handleInputChange} rows={3}
                      placeholder="Contoh: BCA - 1234567890 - Nama Pemilik" className={INPUT + ' resize-none'} />
                  </div>
                  <button onClick={handleSaveProfile} disabled={saving}
                    className="w-full bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50">
                    {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
                  </button>
                </div>
              </div>

              <div className="border-t border-[#e5e5e5] pt-6">
                <h3 className="text-[15px] font-semibold text-[#3b3b3b] mb-4">Ubah Password</h3>
                <div className="space-y-4">
                  {[
                    { name: 'current_password', label: 'Password Saat Ini',       placeholder: 'Masukkan password saat ini' },
                    { name: 'new_password',      label: 'Password Baru',           placeholder: 'Minimal 6 karakter' },
                    { name: 'confirm_password',  label: 'Konfirmasi Password Baru', placeholder: 'Ulangi password baru' },
                  ].map(({ name, label, placeholder }) => (
                    <div key={name}>
                      <label className={LABEL}>{label}</label>
                      <input type="password" name={name} value={passwordData[name]} onChange={handlePasswordChange} placeholder={placeholder} className={INPUT} />
                    </div>
                  ))}
                  <button onClick={handleChangePassword} disabled={changingPassword}
                    className="w-full bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50">
                    {changingPassword ? 'Mengubah...' : 'Ubah Password'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
